import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  setupTwoFactor,
  verifyTwoFactorSetup,
  disableTwoFactor,
} from "../api/authApi";
import {
  setupStaffTwoFactor,
  verifyStaffTwoFactorSetup,
  disableStaffTwoFactor,
} from "../api/staffApi"; // ✅ see staffApi.js additions below
import "./css/TwoFactorAuth.css"; // optional — style it however you like

// Reads the logged-in role from the same "admin" localStorage blob Sidebar.jsx uses
const getLoggedInRole = () => {
  const data = localStorage.getItem("admin");
  const userData = data ? JSON.parse(data) : null;
  return userData?.role || null;
};

const isOrgAccount = (role) => ["SUPERADMIN"].includes(role); // organization-side Admin schema
// everyone else (HR/NSM/RSM/ASM/TSO/STAFF/ACCOUNTS/BDE/AGENT) is a Staff schema record

const TwoFactorAuth = () => {
  const role = getLoggedInRole();
  const isOrg = isOrgAccount(role);

  // "loading" | "disabled" | "setup" | "enabled"
  const [status, setStatus] = useState("loading");
  const [qrCode, setQrCode] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState(""); // needed to disable
  const [busy, setBusy] = useState(false);

  // We don't have a dedicated "am I 2FA enabled" GET endpoint yet, so we infer
  // it from the admin/staff object already cached in localStorage at login.
  useEffect(() => {
    const data = localStorage.getItem("admin");
    const userData = data ? JSON.parse(data) : null;
    setStatus(userData?.isTwoFactorEnabled ? "enabled" : "disabled");
  }, []);

  // ── Start setup: fetch QR code ────────────────────────────────────────────
  const handleStartSetup = async () => {
    setBusy(true);
    console.log(isOrg, "<<  isOrg");
    try {
      const data = isOrg ? await setupTwoFactor() : await setupStaffTwoFactor();

      if (data.success) {
        setQrCode(data.qrCode);
        setManualKey(data.manualEntryKey);
        setStatus("setup");
      } else {
        toast.error(data.message || "Could not start 2FA setup");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server error");
    } finally {
      setBusy(false);
    }
  };

  // ── Confirm setup: verify first OTP from Authenticator app ──────────────
  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      const data = isOrg
        ? await verifyTwoFactorSetup({ token: otp })
        : await verifyStaffTwoFactorSetup({ token: otp });

      if (data.success) {
        toast.success("2FA enabled successfully!");
        setOtp("");
        setStatus("enabled");

        // ✅ Keep localStorage in sync so Login.jsx knows to ask for TOTP next time
        const raw = localStorage.getItem("admin");
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.isTwoFactorEnabled = true;
          localStorage.setItem("admin", JSON.stringify(parsed));
        }
      } else {
        toast.error(data.message || "Invalid code, try again");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server error");
    } finally {
      setBusy(false);
    }
  };

  // ── Disable 2FA (needs current password) ─────────────────────────────────
  const handleDisable = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter your current password to disable 2FA");
      return;
    }
    setBusy(true);
    try {
      const data = isOrg
        ? await disableTwoFactor({ password })
        : await disableStaffTwoFactor({ password });

      if (data.success) {
        toast.success("2FA disabled");
        setPassword("");
        setStatus("disabled");

        const raw = localStorage.getItem("admin");
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.isTwoFactorEnabled = false;
          localStorage.setItem("admin", JSON.stringify(parsed));
        }
      } else {
        toast.error(data.message || "Could not disable 2FA");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: "32px auto", padding: 24 }}>
      <h2 style={{ marginBottom: 8 }}>Two-Factor Authentication</h2>
      <p style={{ color: "#666", marginBottom: 24, fontSize: 14 }}>
        Add an extra layer of security using Google Authenticator (or any TOTP
        app like Authy).
      </p>

      {status === "loading" && <p>Loading...</p>}

      {/* ── Not enabled yet ── */}
      {status === "disabled" && (
        <div>
          <p style={{ marginBottom: 16 }}>
            2FA is currently <strong style={{ color: "#c0392b" }}>OFF</strong>{" "}
            for your account.
          </p>
          <button
            onClick={handleStartSetup}
            disabled={busy}
            className="login-btn"
            style={{ maxWidth: 220 }}
          >
            {busy ? "Loading..." : "Enable 2FA"}
          </button>
        </div>
      )}

      {/* ── QR shown, waiting for first OTP ── */}
      {status === "setup" && (
        <div>
          <ol style={{ paddingLeft: 20, marginBottom: 16, fontSize: 14 }}>
            <li>Open Google Authenticator on your phone.</li>
            <li>Tap the "+" button → "Scan a QR code".</li>
            <li>Point your camera at the QR code below.</li>
          </ol>

          {qrCode && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <img
                src={qrCode}
                alt="Scan with Google Authenticator"
                style={{ width: 220, height: 220 }}
              />
            </div>
          )}

          <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
            Can't scan? Enter this key manually in the app:
            <br />
            <code
              style={{
                background: "#f5f5f5",
                padding: "4px 8px",
                borderRadius: 4,
                display: "inline-block",
                marginTop: 4,
                wordBreak: "break-all",
              }}
            >
              {manualKey}
            </code>
          </p>

          <form onSubmit={handleConfirmSetup}>
            <input
              type="text"
              placeholder="Enter 6-digit code from app"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              maxLength={6}
              inputMode="numeric"
              className="login-input"
              style={{ marginBottom: 12 }}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="login-btn"
              style={{ maxWidth: 220 }}
            >
              {busy ? "Verifying..." : "Verify & Enable"}
            </button>
          </form>
        </div>
      )}

      {/* ── Already enabled ── */}
      {status === "enabled" && (
        <div>
          <p style={{ marginBottom: 16 }}>
            2FA is currently <strong style={{ color: "#27ae60" }}>ON</strong>{" "}
            for your account. You'll be asked for a code from your authenticator
            app every time you log in.
          </p>

          <form onSubmit={handleDisable}>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
              Enter your password to disable 2FA:
            </p>
            <input
              type="password"
              placeholder="Current password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              style={{ marginBottom: 12 }}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="login-btn"
              style={{ maxWidth: 220, background: "#c0392b" }}
            >
              {busy ? "Disabling..." : "Disable 2FA"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuth;
